import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, formatDateUK, apiError } from "@/lib/api";
import ReviewPanel from "@/components/ReviewPanel";
import AuditTimeline from "@/components/AuditTimeline";
import { Breakdown } from "@/pages/CashClosingFormPage";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Plus } from "lucide-react";

const STATUS = { submitted: "Waiting for manager", approved: "Closed", rejected: "Returned — recount", voided: "Voided" };
const CLS = { submitted: "bg-amber-50 text-amber-700", approved: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-700", voided: "bg-navy/5 text-navy/40" };
const diffText = (d) => (d === 0 ? "Exact" : `${d < 0 ? "Short" : "Over"} ${formatGBP(Math.abs(d))}`);

export function CashClosingsPage() {
  const { activeStoreId, stores } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const q = activeStoreId && activeStoreId !== "all" ? `?store_id=${activeStoreId}` : "";
    api.get(`/cash-closings${q}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [activeStoreId]);
  const name = (sid) => stores.find((s) => s.id === sid)?.name || "";
  return (
    <div className="space-y-5" data-testid="cash-closings-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/dashboard")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-xl font-heading font-bold text-navy">Cash closing</h1>
      </div>
      <button onClick={() => navigate("/close-cash")} className="w-full gm-btn-gold flex items-center justify-center gap-2" data-testid="new-cash-closing-btn"><Plus className="w-4 h-4" /> Close today's cash</button>
      {rows === null && <p className="text-sm text-navy/50">Loading…</p>}
      {rows?.length === 0 && <p className="text-sm text-navy/50" data-testid="cash-empty">No cash closings yet.</p>}
      {rows?.map((c) => (
        <button key={c.id} onClick={() => navigate(`/cash-closings/${c.id}`)} className="w-full rounded-xl border border-navy/10 bg-white p-4 flex justify-between items-center text-left" data-testid={`cash-row-${c.id}`}>
          <div><p className="text-sm font-semibold text-navy">{formatDateUK(c.trading_date)}</p><p className="text-xs text-navy/50">{name(c.store_id)} · counted {formatGBP(c.counted_cash)}</p></div>
          <div className="text-right"><p className={`text-xs font-mono ${c.difference === 0 ? "text-navy/60" : "text-red-700"}`}>{diffText(c.difference)}</p>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${CLS[c.status]}`} data-testid={`cash-status-${c.id}`}>{STATUS[c.status]}</span></div>
        </button>
      ))}
    </div>
  );
}

function Recount({ c, onDone }) {
  const [counted, setCounted] = useState("");
  const [why, setWhy] = useState("");
  const go = async () => {
    try { await api.put(`/cash-closings/${c.id}`, { store_id: c.store_id, trading_date: c.trading_date, counted_cash: parseFloat(counted), explanation: why }); toast.success("Recount submitted"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-xl border border-red-200 bg-white p-4 space-y-2" data-testid="cash-recount-panel">
      <Input inputMode="decimal" value={counted} onChange={(e) => setCounted(e.target.value)} placeholder="Cash counted again" className="gm-input font-mono" data-testid="cash-recount-input" />
      <Textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={2} placeholder="Explanation (required if not exact)" data-testid="cash-recount-explanation" />
      <button onClick={go} disabled={counted === ""} className="w-full gm-btn-gold disabled:opacity-40" data-testid="cash-recount-submit-btn">Submit recount</button>
    </div>
  );
}

function OpeningIssue({ c, onDone }) {
  const { user } = useAuth();
  const [why, setWhy] = useState("");
  if (!c.opening_issue) return null;
  const canFix = ["owner", "store_manager"].includes(user?.role) && c.status === "submitted" && !c.opening_exception_by;
  const go = async () => {
    try { await api.post(`/cash-closings/${c.id}/opening-exception`, { reason: why }); toast.success("Exception recorded"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2 text-sm text-red-800" data-testid="cash-opening-issue">
      <p>{c.opening_issue}</p>
      {c.opening_exception_reason ? <p className="text-navy" data-testid="cash-opening-exception">Manager exception: “{c.opening_exception_reason}”</p>
        : <p className="text-xs">This closing cannot be approved until a manager records why the opening cash is right.</p>}
      {canFix && (
        <>
          <Textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={2} placeholder="e.g. Store was closed on Sunday — till untouched" className="bg-white" data-testid="cash-opening-exception-input" />
          <button onClick={go} disabled={why.trim().length < 10} className="w-full gm-btn-navy text-sm disabled:opacity-40" data-testid="cash-opening-exception-btn">Record exception</button>
        </>
      )}
    </div>
  );
}

const OPENING = { carried_forward: "carried from the previous approved closing", opening_balance: "first-day opening cash set by a manager" };

export default function ViewCashClosingPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => { api.get(`/cash-closings/${id}`).then((r) => setC(r.data)).catch(() => setC(false)); }, [id, reload]);
  if (c === false) return <p className="text-sm text-navy/50">Cash closing not found.</p>;
  if (c === false) return <p className="text-sm text-navy/60" data-testid="cash-not-found">This cash closing was not found, or it belongs to a store you cannot see.</p>;
  if (!c) return <p className="text-sm text-navy/50">Loading…</p>;
  const returned = [...(c.history || [])].reverse().find((h) => h.action === "returned");
  return (
    <div className="space-y-5" data-testid="view-cash-closing-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/cash-closings")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
        <div><h1 className="text-xl font-heading font-bold text-navy">Cash closing</h1><p className="text-xs text-navy/50">{c.store_name} · {formatDateUK(c.trading_date)}</p></div>
        <span className={`ml-auto text-xs px-2 py-1 rounded-full ${CLS[c.status]}`} data-testid="cash-detail-status">{STATUS[c.status]}</span>
      </div>
      {c.opening_source === "opening_balance" ? (
        <div className="rounded-xl border border-navy/10 bg-white p-4 text-sm" data-testid="cash-opening-balance">
          <p className="text-navy/60">First-day opening cash</p><p className="font-mono text-lg text-navy">{formatGBP(c.opening_cash)}</p>
          <p className="text-xs text-navy/45 pt-1">{c.explanation}</p>
        </div>
      ) : <Breakdown opening={c.opening_cash} m={c.movements} expected={c.expected_cash} />}
      <OpeningIssue c={c} onDone={() => setReload((n) => n + 1)} />
      {c.historical_reason && <p className="text-xs text-amber-800" data-testid="cash-historical-reason">Historical correction: “{c.historical_reason}”</p>}
      <div className="rounded-xl border border-navy/10 bg-white p-4 space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-navy/60">Counted</span><span className="font-mono text-navy" data-testid="cash-detail-counted">{formatGBP(c.counted_cash)}</span></div>
        <div className="flex justify-between"><span className="text-navy/60">Difference</span><span className="font-mono text-navy font-semibold" data-testid="cash-detail-diff">{diffText(c.difference)}</span></div>
        {c.explanation && <p className="text-navy/70 pt-1" data-testid="cash-detail-explanation">“{c.explanation}”</p>}
        <p className="text-xs text-navy/45 pt-1">Counted by {c.submitted_by_name} · opening {OPENING[c.opening_source] || c.opening_source}</p>
      </div>
      {c.changed_since_closing && <p className="text-xs text-amber-700" data-testid="cash-changed-since">Records for this day changed after closing — expected now {formatGBP(c.expected_now)}.</p>}
      {c.status === "rejected" && returned && <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="cash-returned-reason">Returned by {returned.user_name}: {returned.reason}</p>}
      {c.status === "rejected" && <Recount c={c} onDone={() => setReload((n) => n + 1)} />}
      <ReviewPanel entity="cash-closings" data={c} onDone={() => setReload((n) => n + 1)} />
      <AuditTimeline history={c.history} kind="cash_closing" />
      {user?.role === "employee" && null}
    </div>
  );
}
